import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const ProjectSpectrumCreatives = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    creativeField: '',
    neurodivergent: false,
    interests: '',
    agreeToResearch: false
  });
  const [submitted, setSubmitted] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const galleryImages = [
    //{ id: 1, url: '/images/projects/SPECTRUM.png', caption: '', description: '' },

  ];

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedImage) return;
      
      if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      } else if (e.key === 'Escape') {
        closeLightbox();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    
    // Cleanup
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedImage]);

  // Touch/swipe navigation
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    
    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrevious();
    }
  };

  const openLightbox = (image) => {
    setSelectedImage(image);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'auto';
  };

  const goToPrevious = () => {
    const currentIndex = galleryImages.findIndex(img => img.id === selectedImage.id);
    const previousIndex = currentIndex > 0 ? currentIndex - 1 : galleryImages.length - 1;
    setSelectedImage(galleryImages[previousIndex]);
  };

  const goToNext = () => {
    const currentIndex = galleryImages.findIndex(img => img.id === selectedImage.id);
    const nextIndex = currentIndex < galleryImages.length - 1 ? currentIndex + 1 : 0;
    setSelectedImage(galleryImages[nextIndex]);
  };

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    setSubmitted(true);
  };

  return (
    <>
      {/* Hero Section */}
      <section className="page-hero">
        <div className="container">
          <h1>SPCTRM Open Studio Sessions</h1>
          <p className="hero-cta">A Research Workshop for Neurodivergent Creatives</p>
        </div>
      </section>

      {/* Main Content */}
      <section className="project-detail-content wide-container">
        <div className="container">
          {/* Introduction */}
          <div className="project-section highlight-box">
            <h2>What is SPCTRM CREATIVES?</h2>
            <p className="lead-text">As part of the NeuroArts research initiative, Ziraya Institute is inviting creatives to participate in our first SPCTRM Open Studio Sessions. A small, informal mixer exploring neurodivergence within the Kenyan creative sector.</p>
            <p>These sessions are designed as open conversations rather than formal seminars. Participants will gather in a relaxed environment to share experiences, insights, and challenges related to creativity, collaboration, and neurodivergence in artistic workspaces.</p>
            <p>The goal is to collect lived experiences that will inform future research, policy recommendations, and support systems for neurodivergent creatives in Kenya.</p>
          </div>

          {/* Who Can Participate */}
          <div className="project-two-col">
            <div className="project-col">
              <h2>Participants may include:</h2>
              <ul className="project-list">
                <li>Artists</li>
                <li>Writers</li>
                <li>Musicians</li>
                <li>Actors</li>
                <li>Designers</li>
                <li>Filmmakers</li>
                <li>Creative entrepreneurs</li>
              </ul>
            </div>
            <div className="project-col">
              <h2>How It Works</h2>
              <p>Anyone working within the creative economy who identifies as neurodivergent or is interested in the topic.</p>
              <p>The sessions will be small, conversational, and community-oriented, allowing participants to speak openly about their experiences.</p>
            </div>
          </div>

          {/* Resources Section */}
          <div className="project-section">
            <h2>Practical Resources for Neurodivergent Creatives in Kenya</h2>

            <div className="resources-grid">
              <div className="resource-category">
                <h3>Somatic & Mind-Body Practices</h3>
                <ul>
                  <li><strong>Movement & Stretching:</strong> Daily stretches, yoga, or tai chi to manage stress and enhance focus.</li>
                  <li><strong>Grounding & Breath Work:</strong> Box breathing (inhale 4, hold 4, exhale 4, hold 4) or 4-7-8 breathing exercises to calm the nervous system.</li>
                  <li><strong>Body Scan Meditations:</strong> Short practices to notice tension and release it before or after rehearsals.</li>
                </ul>
              </div>

              <div className="resource-category">
                <h3>Digital & Creative Tools</h3>
                <ul>
                  <li><strong>AI-Assisted Writing:</strong> Tools like ChatGPT or Sudowrite for brainstorming, dialogue, or plot development.</li>
                  <li><strong>Digital Art & Design:</strong> Procreate, Krita, Canva, or Adobe Creative Cloud for low-pressure creation.</li>
                  <li><strong>Music & Audio Production:</strong> GarageBand, Audacity, FL Studio, or BandLab for composing or experimenting.</li>
                  <li><strong>Accessibility Tools:</strong> Screen readers, voice-to-text tools, or focus apps like Forest or Focus@Will.</li>
                </ul>
              </div>

              <div className="resource-category">
                <h3>Therapeutic Supports</h3>
                <ul>
                  <li><strong>Counseling & Coaching:</strong> Local psychologists or creative coaches specializing in neurodivergence.</li>
                  <li><strong>Peer Mentorship:</strong> Connections with other neurodivergent creatives to share strategies.</li>
                  <li><strong>Journaling & Reflection:</strong> Use prompts for emotional processing, line rehearsal, or creative ideation.</li>
                </ul>
              </div>

              <div className="resource-category">
                <h3>Community & Structural Supports</h3>
                <ul>
                  <li><strong>Quiet Rehearsal Spaces:</strong> Partner with art spaces, theaters, and studios to provide low-stimulation zones.</li>
                  <li><strong>Flexible Scheduling & Deadlines:</strong> Encourage productions to respect varied work rhythms.</li>
                  <li><strong>Policy & Advocacy:</strong> Engage in co-creating inclusive guidelines for creative workspaces.</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Registration Form */}
          <div className="section-divider" id="register">
            <h2 style={{ textAlign: 'center' }}>Register for Our Open Studio Sessions</h2>

            {submitted ? (
              <div className="success-message">
                <h3>Thank you for your interest!</h3>
                <p>Participants will be contacted with details about upcoming conversation sessions, including location and time.</p>
                <button onClick={() => setSubmitted(false)} className="btn btn-secondary">
                  Register Another Person
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="registration-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="fullName">Full Name *</label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone">Phone Number (optional)</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="creativeField">Creative Field *</label>
                    <input
                      type="text"
                      id="creativeField"
                      name="creativeField"
                      value={formData.creativeField}
                      onChange={handleChange}
                      required
                      placeholder="e.g., Writer, Musician, Actor..."
                    />
                  </div>
                </div>

                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="neurodivergent"
                      checked={formData.neurodivergent}
                      onChange={handleChange}
                    />
                    Are you comfortable sharing whether you identify as neurodivergent? (optional)
                  </label>
                </div>

                <div className="form-group">
                  <label htmlFor="interests">What interests you about this conversation? *</label>
                  <textarea
                    id="interests"
                    name="interests"
                    rows="4"
                    value={formData.interests}
                    onChange={handleChange}
                    required
                    placeholder="Share what you'd like to explore or discuss..."
                  ></textarea>
                </div>

                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="agreeToResearch"
                      checked={formData.agreeToResearch}
                      onChange={handleChange}
                    />
                    I agree to participate in research conversations that may inform future publications or research outputs.
                  </label>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn btn-large">
                    Register for a Creative Conversation
                  </button>
                </div>

                <div className="form-footer">
                  <p>After registering, you will receive a confirmation email acknowledging your registration. Once participant numbers are confirmed, venue details and the exact event date will be communicated to registered participants via email.</p>
                </div>
              </form>
            )}
          </div>

          {galleryImages.length > 0 && (
            <div className="journal-gallery">
              <h2>Gallery</h2>
              <p className="gallery-subtitle">Moments from our Open Studio Sessions and community gatherings</p>
              <div className="gallery-grid">
                {galleryImages.map((image) => (
                  <div 
                    key={image.id} 
                    className="gallery-item"
                    onClick={() => openLightbox(image)}
                  >
                    <div className="gallery-image-wrapper">
                      <img 
                        src={image.url} 
                        alt={image.caption} 
                        className="gallery-image"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = '/images/placeholder.jpg';
                        }}
                      />
                    </div>
                    {image.caption && (
                      <div className="gallery-caption">
                        <h4>{image.caption}</h4>
                        {image.description && <p>{image.description}</p>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Back Button */}
          <div className="back-button-container section-divider">
            <Link to="/projects" className="btn btn-secondary">
              <i className="fas fa-arrow-left"></i> Back to Projects
            </Link>
          </div>
        </div>
      </section>

      {selectedImage && (
        <div 
          className="lightbox-modal" 
          onClick={closeLightbox}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox}>
              <i className="fas fa-times"></i>
            </button>
            
            {/* Previous Button */}
            <button className="lightbox-nav prev" onClick={goToPrevious}>
              <i className="fas fa-chevron-left"></i>
            </button>
            
            {/* Next Button */}
            <button className="lightbox-nav next" onClick={goToNext}>
              <i className="fas fa-chevron-right"></i>
            </button>
            
            <div className="lightbox-image-wrapper">
              <img 
                src={selectedImage.url} 
                alt={selectedImage.caption} 
                className="lightbox-image"
              />
            </div>
            
            {/* Image counter */}
            <div className="lightbox-counter">
              {galleryImages.findIndex(img => img.id === selectedImage.id) + 1} / {galleryImages.length}
            </div>
            
            {(selectedImage.caption || selectedImage.description) && (
              <div className="lightbox-caption">
                <h3>{selectedImage.caption}</h3>
                <p>{selectedImage.description}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ProjectSpectrumCreatives;